import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MIN_PLACE_QUERY_LENGTH,
  resolvePlace,
  usePlaceSearch,
  type PlacePrediction,
  type PlaceSummary,
} from '../../../../lib/use-place-search';
import { highlightMatches } from '../../../../lib/highlight-matches';
import { newPlaceSessionToken } from '../../../../lib/place-session-token';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import styles from './PlacePicker.module.css';
import { IconButton, ScrollArea } from '../../ui';
import { RailCatcher } from '../../../RailCatcher';

/**
 * 250ms — the same figure both native pickers use, so the three surfaces feel identical. Long enough
 * that a typed word is one request rather than five, short enough that the list feels attached to the
 * keyboard.
 */
const DEBOUNCE_MS = 250;

/**
 * Atlas will return more, but a picker is scanned, not read. Past about eight rows the list stops
 * helping the user choose and starts asking them to work.
 */
const MAX_ROWS = 8;

// The composer's "+ Location" picker — a centered modal (mirrors the ContactPicker) over Wix Atlas'
// TYPEAHEAD. Typing lists predictions (no coordinates); picking one resolves just that row to a
// lat/lng and inserts a maps link line into the composer draft.
export function PlacePicker({ onPick, onClose }: { onPick: (place: PlaceSummary) => void; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { logModalActionBi } = useGenericBi('place-picker');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveFailed, setResolveFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /**
   * One session per opening of the picker: every keystroke's prediction call and the single resolve that
   * ends it carry the same token, which is how Atlas (and Google beneath it) bills one search rather than
   * one lookup per character. A fresh token next time the picker opens — a concluded session's token is
   * spent.
   */
  const sessionToken = useRef<string | null>(null);
  if (!sessionToken.current) sessionToken.current = newPlaceSessionToken();

  const language = i18n.language;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const search = usePlaceSearch(debounced, { language, sessionToken: sessionToken.current });
  useLoadBi('place-picker-data', {
    isLoading: search.status === 'loading',
    isError: search.status === 'error',
  });

  /**
   * Resolving happens HERE rather than in the caller, so the composer keeps taking a finished place with
   * coordinates and needs no knowledge of the two-step at all.
   */
  const pick = async (prediction: PlacePrediction) => {
    if (resolvingId) return;
    logModalActionBi({ page_name: 'chat', modal_name: 'place_picker', action: 'pick_place' });
    setResolvingId(prediction.searchId);
    setResolveFailed(false);
    try {
      const place = await resolvePlace(prediction.searchId, { language, sessionToken: sessionToken.current });
      onPick(place);
      onClose();
    } catch {
      // Includes the expected case of a `searchId` that aged out. Either way the picker stays open with
      // its results intact so the user can pick again, which is the only useful next move.
      setResolvingId(null);
      setResolveFailed(true);
    }
  };

  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <RailCatcher onClose={onClose} zIndex={60} />
      <div role="dialog" aria-modal="true" aria-label={t('chat.locationPicker.title')} className={styles.card}>
        <div className={styles.header}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setResolveFailed(false);
            }}
            placeholder={t('chat.locationPicker.search')}
          />
          <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </IconButton>
        </div>
        <div className={styles.divider} aria-hidden />
        <ScrollArea className={styles.body}>
          <Results
            state={search}
            hasQuery={debounced.length >= MIN_PLACE_QUERY_LENGTH}
            resolvingId={resolvingId}
            resolveFailed={resolveFailed}
            onPick={pick}
          />
        </ScrollArea>
      </div>
    </div>,
    document.body,
  );
}

function Results({
  state,
  hasQuery,
  resolvingId,
  resolveFailed,
  onPick,
}: {
  state: ReturnType<typeof usePlaceSearch>;
  hasQuery: boolean;
  resolvingId: string | null;
  resolveFailed: boolean;
  onPick: (prediction: PlacePrediction) => void;
}) {
  const { t } = useTranslation();
  if (state.status === 'loading') {
    return (
      <>
        {[72, 60, 84].map((w, i) => (
          <div key={i} className={styles.shimmerRow}>
            <div className="orion-shimmer" style={{ height: 14, width: `${w}%`, borderRadius: 6 }} />
          </div>
        ))}
      </>
    );
  }
  if (state.status === 'error') return <div className={styles.empty}>{t('chat.locationPicker.error')}</div>;
  // Before the user has typed enough to search, the empty state shows the prompt rather than "no results" —
  // one character has found nothing because nothing was asked, not because nothing is there.
  if (state.predictions.length === 0 && !hasQuery) return <div className={styles.empty}>{t('chat.locationPicker.search')}</div>;
  if (state.predictions.length === 0) return <div className={styles.empty}>{t('chat.locationPicker.empty')}</div>;
  return (
    <>
      {/* A pick that failed to resolve leaves the list up: the user's next move is to pick again. */}
      {resolveFailed && <div className={styles.rowError}>{t('chat.locationPicker.error')}</div>}
      {state.predictions.slice(0, MAX_ROWS).map((prediction) => (
        <PlaceRow
          key={prediction.searchId}
          prediction={prediction}
          resolving={resolvingId === prediction.searchId}
          disabled={resolvingId !== null}
          onPick={onPick}
        />
      ))}
    </>
  );
}

function PlaceRow({
  prediction,
  resolving,
  disabled,
  onPick,
}: {
  prediction: PlacePrediction;
  resolving: boolean;
  disabled: boolean;
  onPick: (prediction: PlacePrediction) => void;
}) {
  return (
    <button
      type="button"
      className={styles.row}
      disabled={disabled}
      aria-busy={resolving || undefined}
      onClick={() => onPick(prediction)}
    >
      <span className={styles.pin} aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </span>
      <span className={styles.rowText}>
        {/* The place, with the characters the user typed picked out — that is what makes a list of eight
            scannable rather than a wall. */}
        <span className={styles.rowName}>
          {highlightMatches(prediction.mainText, prediction.matched).map((segment, i) =>
            segment.match ? (
              <b key={i} className={styles.matched}>
                {segment.text}
              </b>
            ) : (
              <span key={i}>{segment.text}</span>
            ),
          )}
        </span>
        {/* Where it is. "Salame St" alone is ambiguous; with its locality beneath it, it is an address. */}
        {prediction.secondaryText && <span className={styles.rowMeta}>{prediction.secondaryText}</span>}
      </span>
    </button>
  );
}
